<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { routes } from '@app/router/routes'
import Box from '@uiKit/elements/Box.vue'
import { formatSubmittedAt, getIdempotencyKey, maskAccountNumber } from '@shared/utils'
import FiatWalletTab from '../components/FiatWalletTab.vue'
import WithdrawalSuccessModal from '../components/WithdrawalSuccessModal.vue'
import WithdrawalErrorModal from '../components/WithdrawalErrorModal.vue'
import TransactionDetailDrawer from '../../transactions/components/TransactionDetailDrawer.vue'
import { useTransactionDrawer } from '../../transactions/composables/useTransactionDrawer'
import { useTransactionsQuery } from '../../transactions/api/queries/useTransactionsQuery'
import { useTransactionsFiltersQuery } from '../../transactions/api/queries/useTransactionsFiltersQuery'
import { useDownloadTransactionsMutation } from '../../transactions/api/useDownloadTransactionsMutation'
import type {
  SortOption,
  TransactionFilters,
} from '../../transactions/types/transactionTypes'
import { useFiatWalletQuery } from '../api/queries/useFiatWalletQuery'
import { useSlicealAccountQuery } from '../api/queries/useSlicealAccountQuery'
import { useSubmitWithdrawalMutation } from '../api/mutations/useSubmitWithdrawalMutation'
import { useAddFundsMutation } from '../api/mutations/useAddFundsMutation'
import { canTransactInWallet } from '../utils/walletAccessUtils'
import {
  useBankAccountsQuery,
  useAccountAccessRestriction,
  useKycActionState,
} from '@modules/account'

const router = useRouter()
const route = useRoute()

const { isRestricted } = useAccountAccessRestriction()
const { kycActionState } = useKycActionState()

const fiatQuery = useFiatWalletQuery()
const bankAccountsQuery = useBankAccountsQuery()
const slicealAccountQuery = useSlicealAccountQuery()

const currency = computed(() => fiatQuery.display.value?.currency ?? 'AED')
const balance = computed(() => fiatQuery.display.value?.balance ?? '')
const balanceAmount = computed(() =>
  parseFloat((fiatQuery.display.value?.balance ?? '0').replace(/,/g, '')),
)
const lastUpdated = computed(() => fiatQuery.display.value?.lastUpdated ?? '')
const walletLoading = computed(() => fiatQuery.isLoading.value)
const walletHasError = computed(() => fiatQuery.isError.value)

const onRetryWallet = () => {
  fiatQuery.refetch()
}

const bankAccounts = computed(() => bankAccountsQuery.data.value ?? [])
const hasBankAccount = computed(() => bankAccounts.value.length > 0)
const hasActiveBank = computed(() =>
  bankAccounts.value.some((account) => account.accountStatus === 'verified'),
)
const defaultBankAccount = computed(
  () => bankAccounts.value.find((account) => account.isPrimary) ?? bankAccounts.value[0] ?? null,
)

const withdrawMutation = useSubmitWithdrawalMutation()
const withdrawing = computed(() => withdrawMutation.isPending.value)

const addFundsMutation = useAddFundsMutation()
const depositSubmitting = computed(() => addFundsMutation.isPending.value)
const depositError = ref<string | null>(null)
const fiatWalletTabRef = ref<InstanceType<typeof FiatWalletTab> | null>(null)

const withdrawSuccessVisible = ref(false)
const withdrawErrorVisible = ref(false)
const withdrawErrorMessage = ref('')
const withdrawResult = ref<{
  amount: string
  bankName: string
  maskedAccount: string
  submittedAt: string
} | null>(null)

// ── Activity feed (filters + sort + paged data) ────────────────────────────
// Lifted from the now-removed standalone /wallet/transactions page so the
// "Recent Transactions" card on the wallet page renders the live activity
// feed inline (previously this card showed an empty "No transactions yet"
// placeholder).
const activeFilters = reactive<TransactionFilters>({
  search: '',
  type: '',
  status: '',
  sort: 'newest_first',
  dateFrom: '',
  dateTo: '',
})

const queryFilters = computed<TransactionFilters>(() => ({ ...activeFilters }))

const transactionsQuery = useTransactionsQuery(queryFilters)
const transactionsFiltersQuery = useTransactionsFiltersQuery()
const downloadMutation = useDownloadTransactionsMutation()

const activityTransactions = computed(
  () => transactionsQuery.data.value?.pages.flatMap((page) => page.rows) ?? [],
)
const activityHasMore = computed(() => Boolean(transactionsQuery.hasNextPage.value))
const activityLoading = computed(() => transactionsQuery.isLoading.value)
const activityLoadingMore = computed(() => transactionsQuery.isFetchingNextPage.value)

const filterOptionsData = computed(() => transactionsFiltersQuery.data.value)
const typeOptions = computed(() => filterOptionsData.value?.types ?? [])
const statusOptions = computed(() => filterOptionsData.value?.statuses ?? [])

const sortOptions = [
  { value: 'newest_first', label: 'Newest First' },
  { value: 'oldest_first', label: 'Oldest First' },
  { value: 'highest_amount', label: 'Highest Amount' },
  { value: 'lowest_amount', label: 'Lowest Amount' },
]

const activeFilterCount = computed(() => {
  let count = 0
  if (activeFilters.type) count++
  if (activeFilters.status) count++
  if (activeFilters.dateFrom || activeFilters.dateTo) count++
  return count
})

const downloadError = ref('')

const onApplyFilters = (next: TransactionFilters) => {
  activeFilters.type = next.type
  activeFilters.status = next.status
  activeFilters.dateFrom = next.dateFrom
  activeFilters.dateTo = next.dateTo
  activeFilters.sort = next.sort
}

const onClearFilters = () => {
  activeFilters.type = ''
  activeFilters.status = ''
  activeFilters.dateFrom = ''
  activeFilters.dateTo = ''
}

const onApplySort = (value: string) => {
  activeFilters.sort = value as SortOption
}

const onLoadMore = () => {
  if (transactionsQuery.hasNextPage.value && !transactionsQuery.isFetchingNextPage.value) {
    transactionsQuery.fetchNextPage()
  }
}

const onExportCsv = async () => {
  downloadError.value = ''
  try {
    await downloadMutation.mutateAsync({ format: 'csv', filters: queryFilters.value })
  } catch (e) {
    downloadError.value =
      (e as { message?: string })?.message ?? 'Failed to download. Please try again.'
  }
}

const onExportExcel = async () => {
  downloadError.value = ''
  try {
    await downloadMutation.mutateAsync({ format: 'excel', filters: queryFilters.value })
  } catch (e) {
    downloadError.value =
      (e as { message?: string })?.message ?? 'Failed to download. Please try again.'
  }
}

const onDownloadPopoverToggle = (open: boolean) => {
  if (!open) downloadError.value = ''
}

// ── Deposit / withdraw mutations ───────────────────────────────────────────
const onDepositConfirm = async (payload: {
  amount: number
  referenceId: string
  attachment: File
}) => {
  depositError.value = null
  const idempotencyKey = `SL-DEP-${getIdempotencyKey()}`
  try {
    await addFundsMutation.mutateAsync({
      amount: payload.amount,
      idempotencyKey,
      utrNumber: payload.referenceId,
      transactionDocument: payload.attachment,
    })
    fiatWalletTabRef.value?.closeDeposit()
    router.push({
      path: routes.WALLET_DEPOSIT_SUCCESS,
      query: {
        referenceId: idempotencyKey,
        amount: payload.amount.toString(),
        submittedAt: formatSubmittedAt(),
      },
    })
  } catch (e) {
    depositError.value =
      (e as { message?: string })?.message ?? 'Failed to confirm transfer. Please try again.'
  }
}

const onDepositClearError = () => {
  depositError.value = null
}

const onWithdraw = async (payload: { amount: number; bankAccountId: string }) => {
  const bank =
    bankAccounts.value.find((account) => account.id === payload.bankAccountId) ??
    defaultBankAccount.value
  try {
    await withdrawMutation.mutateAsync({
      amount: payload.amount,
      idempotencyKey: getIdempotencyKey(),
      bankAccountId: payload.bankAccountId,
    })
    const formattedAmount = payload.amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    withdrawResult.value = {
      amount: formattedAmount,
      bankName: bank?.bankName ?? '—',
      maskedAccount: maskAccountNumber(bank?.accountNumber),
      submittedAt: formatSubmittedAt(),
    }
    withdrawSuccessVisible.value = true
  } catch (e) {
    withdrawErrorMessage.value =
      (e as { message?: string })?.message ?? 'Something went wrong. Please try again.'
    withdrawErrorVisible.value = true
  }
}

const onWithdrawDone = () => {
  withdrawSuccessVisible.value = false
}

const onAddBank = () => {
  router.push(routes.ADD_BANK_ACCOUNT)
}

const {
  selectedTransaction,
  drawerOpen,
  detail,
  detailLoading,
  detailError,
  openDrawer,
  closeDrawer,
} = useTransactionDrawer()

// Deep-link entry: callers from outside (dashboard, transactions empty state)
// navigate to /wallet?action=deposit. Auto-open the deposit drawer once the
// tab is mounted, then strip the query so back-navigation doesn't re-open it.
watch(
  () => route.query.action,
  (action) => {
    if (action !== 'deposit') return
    // Honour the same money-moving gate the Quick Actions buttons use — an
    // account that can't transact (restricted, KYC action-required, or in-review
    // without a verified bank) ignores the deep-link entry, but still strips the
    // query so it doesn't keep re-triggering.
    if (
      !canTransactInWallet({
        restricted: isRestricted.value,
        kycState: kycActionState.value,
        hasBankAccount: hasBankAccount.value,
        hasActiveBank: hasActiveBank.value,
      })
    ) {
      const cleaned = { ...route.query }
      delete cleaned.action
      router.replace({ path: route.path, query: cleaned })
      return
    }
    fiatWalletTabRef.value?.openDeposit()
    const nextQuery = { ...route.query }
    delete nextQuery.action
    router.replace({ path: route.path, query: nextQuery })
  },
  { immediate: true, flush: 'post' },
)
</script>

<template>
  <Box direction="col" class="fiat-wallet-container">
    <FiatWalletTab
      ref="fiatWalletTabRef"
      :restricted="isRestricted"
      :kyc-state="kycActionState"
      :currency="currency"
      :balance="balance"
      :balance-amount="balanceAmount"
      :bank-accounts="bankAccounts"
      :default-bank-account="defaultBankAccount"
      :sliceal-account="slicealAccountQuery.data.value ?? null"
      :last-updated="lastUpdated"
      :activity-transactions="activityTransactions"
      :activity-loading="activityLoading"
      :activity-loading-more="activityLoadingMore"
      :activity-has-more="activityHasMore"
      :filters="activeFilters"
      :type-options="typeOptions"
      :status-options="statusOptions"
      :sort-options="sortOptions"
      :active-filter-count="activeFilterCount"
      :downloading="downloadMutation.isPending.value"
      :download-error="downloadError"
      :wallet-loading="walletLoading"
      :wallet-has-error="walletHasError"
      :withdrawing="withdrawing"
      :deposit-submitting="depositSubmitting"
      :deposit-error="depositError"
      @deposit-confirm="onDepositConfirm"
      @deposit-clear-error="onDepositClearError"
      @withdraw="onWithdraw"
      @add-bank="onAddBank"
      @view-details="openDrawer"
      @retry-wallet="onRetryWallet"
      @apply-filters="onApplyFilters"
      @clear-filters="onClearFilters"
      @apply-sort="onApplySort"
      @load-more="onLoadMore"
      @export-csv="onExportCsv"
      @export-excel="onExportExcel"
      @download-popover-toggle="onDownloadPopoverToggle"
    />

    <TransactionDetailDrawer
      :transaction="selectedTransaction"
      :open="drawerOpen"
      :detail="detail"
      :detail-loading="detailLoading"
      :detail-error="detailError"
      @close="closeDrawer"
    />

    <WithdrawalSuccessModal
      v-if="withdrawResult"
      v-model:visible="withdrawSuccessVisible"
      :amount="withdrawResult.amount"
      :bank-name="withdrawResult.bankName"
      :masked-account="withdrawResult.maskedAccount"
      :submitted-at="withdrawResult.submittedAt"
      @done="onWithdrawDone"
    />

    <WithdrawalErrorModal
      v-model:visible="withdrawErrorVisible"
      :message="withdrawErrorMessage"
    />
  </Box>
</template>

<style scoped>
.fiat-wallet-container {
  height: 100%;
  min-height: 0;
}
</style>
